import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Database, Clock, CheckCircle, AlertTriangle, Bell } from 'lucide-react';

const CierrePeriodoWidget = ({ className = '' }) => {
  const [cierrePeriodoData, setCierrePeriodoData] = useState(null);
  const [showCierreDialog, setShowCierreDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUrgentToast, setShowUrgentToast] = useState(false);

  useEffect(() => {
    loadCierrePeriodoData();
  }, []);

  // Check for urgent periods and show toast notification
  useEffect(() => {
    if (cierrePeriodoData && !showUrgentToast) {
      const urgentPeriods = cierrePeriodoData.periodos?.filter(p => 
        p.estado === 'por_vencer' || (p.dias_para_vencer !== undefined && p.dias_para_vencer <= 7 && p.dias_para_vencer >= 0)
      );
      
      if (urgentPeriods?.length > 0) {
        setShowUrgentToast(true);
        toast.warning(
          `⚠️ ${urgentPeriods.length} período(s) próximo(s) a vencer`,
          {
            description: 'Revisa el estado de cierre de períodos',
            duration: 8000,
            action: {
              label: 'Ver',
              onClick: () => setShowCierreDialog(true)
            }
          }
        );
      }
    }
  }, [cierrePeriodoData, showUrgentToast]);

  const loadCierrePeriodoData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/cierre-periodo/dashboard');
      setCierrePeriodoData(res.data);
    } catch (error) {
      console.error('Error loading cierre periodo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarCierre = async (periodo, tipo, completo) => {
    try {
      await api.post('/cierre-periodo/marcar', {
        periodo,
        tipo,
        completo,
        comentarios: null
      });
      toast.success(`Período ${periodo} ${completo ? 'cerrado' : 'reabierto'}`);
      loadCierrePeriodoData();
    } catch (error) {
      toast.error('Error al actualizar estado del período');
    }
  };

  if (loading) {
    return (
      <Card className={`p-5 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Card>
    );
  }

  if (!cierrePeriodoData) {
    return null;
  }

  // Count urgent and overdue periods
  const urgentCount = cierrePeriodoData?.periodos?.filter(p => 
    p.estado === 'por_vencer' || (p.dias_para_vencer !== undefined && p.dias_para_vencer <= 7 && p.dias_para_vencer >= 0)
  ).length || 0;
  
  const overdueCount = cierrePeriodoData?.periodos?.filter(p => p.estado === 'vencido').length || 0;

  return (
    <>
      <Card className={`p-5 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200 ${className}`} data-testid="cierre-periodo-widget">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${overdueCount > 0 ? 'bg-red-100' : urgentCount > 0 ? 'bg-amber-100' : 'bg-teal-100'}`}>
              {overdueCount > 0 ? (
                <AlertTriangle size={22} className="text-red-600 animate-pulse" />
              ) : urgentCount > 0 ? (
                <Bell size={22} className="text-amber-600 animate-bounce" />
              ) : (
                <Database size={22} className="text-teal-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-800">Estado de Períodos</span>
                {overdueCount > 0 && (
                  <Badge className="bg-red-500 text-white animate-pulse text-xs">
                    {overdueCount} vencido{overdueCount > 1 ? 's' : ''}
                  </Badge>
                )}
                {urgentCount > 0 && overdueCount === 0 && (
                  <Badge className="bg-amber-500 text-white text-xs">
                    {urgentCount} urgente{urgentCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-600">Cierre de datos y presentaciones</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCierreDialog(true)}
            className="text-teal-700 border-teal-300 hover:bg-teal-100"
          >
            Ver más
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cierrePeriodoData.periodos?.slice(0, 4).map((p) => {
            const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const mes = parseInt(p.periodo.substring(4, 6));
            const año = p.periodo.substring(0, 4);
            const periodoLabel = `${meses[mes]} ${año}`;
            const isUrgent = p.estado === 'por_vencer' || (p.dias_para_vencer !== undefined && p.dias_para_vencer <= 7 && p.dias_para_vencer >= 0);
            const isOverdue = p.estado === 'vencido';
            
            return (
              <div 
                key={p.periodo} 
                className={`bg-white rounded-lg p-3 border-2 transition-all ${
                  isOverdue ? 'border-red-400 shadow-red-100 shadow-md animate-pulse' :
                  isUrgent ? 'border-amber-400 shadow-amber-100 shadow-md' :
                  'border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    {isOverdue && <AlertTriangle size={14} className="text-red-500" />}
                    {isUrgent && !isOverdue && <Bell size={14} className="text-amber-500 animate-bounce" />}
                    <span className="font-medium text-slate-800">{periodoLabel}</span>
                  </div>
                  <Badge 
                    className={
                      p.estado === 'presentado' ? 'bg-green-100 text-green-700' :
                      p.estado === 'vencido' ? 'bg-red-100 text-red-700 animate-pulse' :
                      p.estado === 'por_vencer' ? 'bg-amber-100 text-amber-700' :
                      p.estado === 'listo' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }
                  >
                    {p.estado === 'presentado' ? 'Presentado' :
                     p.estado === 'vencido' ? '⚠️ Vencido' :
                     p.estado === 'por_vencer' ? '🔔 Por vencer' :
                     p.estado === 'listo' ? 'Listo' :
                     p.estado === 'generado' ? 'Generado' : 'Pendiente'}
                  </Badge>
                </div>
                {/* Days indicator */}
                {p.dias_para_vencer !== undefined && p.dias_para_vencer <= 7 && (
                  <p className={`text-xs mb-2 font-medium ${
                    p.dias_para_vencer < 0 ? 'text-red-600' :
                    p.dias_para_vencer === 0 ? 'text-red-600' :
                    p.dias_para_vencer <= 3 ? 'text-amber-600' :
                    'text-slate-500'
                  }`}>
                    {p.dias_para_vencer < 0 ? `Vencido hace ${Math.abs(p.dias_para_vencer)} días` :
                     p.dias_para_vencer === 0 ? '¡Vence HOY!' :
                     `Vence en ${p.dias_para_vencer} días`}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                    className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                      p.cierre.movimientos_completo 
                        ? 'bg-green-500 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    title="Movimientos"
                  >
                    Mov
                  </button>
                  <button
                    onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                    className={`flex-1 p-1.5 rounded text-xs font-medium transition-colors ${
                      p.cierre.padron_completo 
                        ? 'bg-green-500 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    title="Padrón"
                  >
                    Pad
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Diálogo de Cierre de Período */}
      <Dialog open={showCierreDialog} onOpenChange={setShowCierreDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Database className="text-teal-600" size={20} />
              Estado de Cierre de Períodos
            </DialogTitle>
            <DialogDescription>
              Gestione el cierre de datos de movimientos y padrón por período
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            <div className="grid gap-3">
              {cierrePeriodoData?.periodos?.map((p) => {
                const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                const mes = parseInt(p.periodo.substring(4, 6));
                const año = p.periodo.substring(0, 4);
                const periodoLabel = `${meses[mes]} ${año}`;
                
                return (
                  <div key={p.periodo} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-semibold text-slate-900">{periodoLabel}</p>
                          <p className="text-xs text-slate-500">
                            Vence: {new Date(p.fecha_vencimiento).toLocaleDateString('es-ES')}
                            {p.dias_para_vencer > 0 ? ` (${p.dias_para_vencer} días)` : 
                             p.dias_para_vencer === 0 ? ' (Hoy)' : 
                             ` (Vencido hace ${Math.abs(p.dias_para_vencer)} días)`}
                          </p>
                        </div>
                        <Badge 
                          className={
                            p.estado === 'presentado' ? 'bg-green-100 text-green-700' :
                            p.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                            p.estado === 'por_vencer' ? 'bg-amber-100 text-amber-700' :
                            p.estado === 'listo' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {p.estado === 'presentado' ? 'Presentado' :
                           p.estado === 'vencido' ? 'Vencido' :
                           p.estado === 'por_vencer' ? 'Por vencer' :
                           p.estado === 'listo' ? 'Listo para generar' :
                           p.estado === 'generado' ? 'Reporte generado' : 'Pendiente'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'movimientos', !p.cierre.movimientos_completo)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            p.cierre.movimientos_completo 
                              ? 'bg-green-500 text-white hover:bg-green-600' 
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {p.cierre.movimientos_completo ? <CheckCircle size={16} /> : <Clock size={16} />}
                          Movimientos
                        </button>
                        <button
                          onClick={() => handleMarcarCierre(p.periodo, 'padron', !p.cierre.padron_completo)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            p.cierre.padron_completo 
                              ? 'bg-green-500 text-white hover:bg-green-600' 
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {p.cierre.padron_completo ? <CheckCircle size={16} /> : <Clock size={16} />}
                          Padrón
                        </button>
                        {p.reportes_generados > 0 && (
                          <Badge variant="outline" className="text-blue-600 border-blue-300">
                            {p.reportes_generados} reporte(s)
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="border-t pt-4 flex justify-between items-center">
            <p className="text-xs text-slate-500">
              Marque como completo cuando los datos estén listos para procesar
            </p>
            <Button variant="outline" onClick={() => setShowCierreDialog(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CierrePeriodoWidget;
